import { Checkbox, Chip, Tooltip } from '@material-ui/core';
import RestoreIcon from '@material-ui/icons/Restore';
import { Button, Modal } from '@vadiun/react-components';
import { MUIDataTableColumnDef } from 'mui-datatables';
import {
  ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useState
} from 'react';
import { Loading, Table } from 'shared/components';
import { v4 as uuidv4 } from 'uuid';
import { ImperativeStepMovementType } from '../stepper/types';
import HeaderSelector from '../table/HeaderSelector';
import { mappingReducer } from '../utils/mappingReducer';
import { ColumnIdType, ColumnMapping, MappingType } from '../utils/types';
import { getDefaultPriorityBoolean, getDefaultPriorityValue } from '../utils/constants';

interface TravelFormMappingProps {
  mappingData: Record<string, string | boolean>[];
  currentMappingData?: Record<string, string | boolean>[];
  currentColSize: number;
  currentColumnMapping?: ColumnMapping[];
  onMappingComplete: (
    data: MappingType[],
    currentColumnMapping: ColumnMapping[],
    currentRawData: Record<string, string | boolean>[]
  ) => Promise<void>;
  onResetMapping: (onPerformReset: () => void) => void;
  columnMapping: ColumnMapping[];
  clientId?: number; // ID del cliente seleccionado
}

const TravelFormMapping = forwardRef<
  ImperativeStepMovementType,
  TravelFormMappingProps
>(
  (
    {
      mappingData: mp,
      currentColSize,
      onMappingComplete,
      currentColumnMapping,
      currentMappingData,
      onResetMapping,
      columnMapping,
      clientId
    },
    ref
  ) => {
    const modifiedColumnMapping = columnMapping.map((col, index) => ({
      ...col,
      columnId: col.isPriority? [index] : col.columnId || []
    }));
    const initialColSize = currentColumnMapping?.length || currentColSize;
    const [mappingData, setMappingData] = useState<Record<string, string | boolean>[]>(
      currentMappingData || mp
    );
    const [colSize, setColSize] = useState<number>(initialColSize);
    const [isMissingColumnOpen, setIsMissingColumnOpen] =
      useState<boolean>(false);
    const [isBEVerifying, setIsBEVerifying] = useState<boolean>(false);
    const [priorityStatus, setPriorityStatus] = useState<boolean[]>(
      new Array(mappingData.length).fill(false)
    );
    const [regressStatus, setRegressStatus] = useState<boolean[]>(
      new Array(mappingData.length).fill(false)
    );

    // Actualizar priorityStatus cuando cambie mappingData
    useEffect(() => {
      // Usar la función helper para obtener el valor de prioridad por defecto según el cliente
      const defaultPriorityValue = getDefaultPriorityBoolean(clientId);
      setPriorityStatus(new Array(mappingData.length).fill(defaultPriorityValue));
      setRegressStatus(new Array(mappingData.length).fill(false));
    }, [mappingData.length, clientId]);

    const reducerColumns = currentColumnMapping || modifiedColumnMapping;
    const [mappingState, mappingDispatch] = useReducer(mappingReducer, {
      columns: reducerColumns,
      columnNumber:
        colSize > reducerColumns.length ? colSize : reducerColumns.length
    });

    const handleChange = (
      event: ChangeEvent<{ name?: string; value: unknown }>,
      columnIndex: number
    ) => {
      if (event.target.value === 'delete') {
        setColSize((current) => --current);
        setMappingData((smp) =>
          smp.map((mdData) => {
            const { [`column${columnIndex}`]: _, ...rest } = mdData;
            return Object.values(rest).reduce(
              (acc, v, index) => ({
                ...acc,
                [`column${index}`]: v
              }),
              {} as Record<string, string | boolean>
            );
          })
        );
        return mappingDispatch({
          type: 'DELETE_COLUMN',
          payload: {
            columnId: columnIndex
          }
        });
      }
      mappingDispatch({
        type: 'SET_COLUMN_TYPE',
        payload: {
          id: event.target.value as ColumnIdType,
          columnId: columnIndex
        }
      });
    };

    const handleRemoveHeaderMapping = (
      columnId: ColumnIdType,
      columnIndex: number
    ) => {
      mappingDispatch({
        type: 'REMOVE_COLUMN_TYPE',
        payload: {
          id: columnId,
          columnId: columnIndex
        }
      });
    };

    const handlePerformResetMapping = () => {
      setMappingData(mp);
      setColSize(currentColSize);
      mappingDispatch({
        type: 'RESET',
        payload: {
          columns: columnMapping.map((col, index) => ({
            ...col,
            columnId: col.isPriority? [index] : col.columnId || []
          })),
          columnNumber: currentColSize
        }
      });
    };

    const handleResetMapping = () => {
      onResetMapping(handlePerformResetMapping);
    };

    const handleSubmit = async () => {
      if (
        mappingState.columns.some(({ mandatory, inUse }) => mandatory && !inUse)
      ) {
        return setIsMissingColumnOpen(true);
      }
      const selectedColumnMapping = mappingState.columns.filter(
        ({ inUse, isPriority }) => inUse || isPriority
      );

      // Filtrar el primer elemento (headers) y procesar solo los datos reales
      
      let data = mappingData.map((currentData) => {
        const mappingRecord = Object.values(currentData).reduce(
          (acc, value, index) => {
            const mappedColumn = selectedColumnMapping.find(({ columnId }) =>
              columnId?.includes(index)
            );
            if (mappedColumn) {
              const currentValue = acc[mappedColumn.id];
              return {
                ...acc,
                [mappedColumn.id]: acc[mappedColumn.id]
                  ? Array.isArray(currentValue)
                    ? [...currentValue, value]
                    : [currentValue, value]
                  : value
              };
            }
            return acc;
          },
          {} as { [key in ColumnIdType]: string | string[] }
        );
        
        // Agregar valores por defecto para las columnas especiales
        selectedColumnMapping.forEach((column) => {
          if (column.isPriority && !mappingRecord[column.id]) {
            // Usar la función helper para obtener el valor de prioridad por defecto según el cliente
            const defaultPriorityValue = getDefaultPriorityValue(clientId);
            mappingRecord[column.id] = defaultPriorityValue;
          }
        });
        
        return mappingRecord;
      });

      // Agregar las columnas especiales con sus valores
      if (priorityColumn) {
        data = data.map((dataElement, index) => ({
          ...dataElement,
          [ColumnIdType.HAS_PRIORITY]: priorityStatus[index] ? "1" : "0"
        }));
      }


      if (
        selectedColumnMapping.every(
          ({ id }) => id !== ColumnIdType.ADVANCED_HOURS
        )
      ) {
        selectedColumnMapping.push({
          ...mappingState.columns.find(
            ({ id }) => id === ColumnIdType.ADVANCED_HOURS
          )!,
          columnId: [Object.keys(data[0]).length]
        });
        data = data.map((dataElement) => {
          return {
            ...dataElement,
            [ColumnIdType.ADVANCED_HOURS]: '01:00'
          };
        });
      }

      try {
        setIsBEVerifying(true);
        await onMappingComplete(
          data,
          selectedColumnMapping
            .sort(({ columnId: cidA }, { columnId: cidB }) => {
              if (cidA[0] > cidB[0]) {
                return 1;
              }
              if (cidA[0] === cidB[0]) {
                return 0;
              }
              return -1;
            })
            .map((cm, index) => ({
              ...cm,
              columnId: [index]
            })),
          data.map((dataRecord) => {
            return Object.values(dataRecord).reduce(
              (acc, stringData, index) => {
                if (Array.isArray(stringData)) {
                  return {
                    ...acc,
                    [`column${index}`]: stringData.join(',')
                  };
                }
                return {
                  ...acc,
                  [`column${index}`]: stringData
                };
              },
              {} as Record<string, string | boolean>
            );
          })
        );
      } finally {
        setIsBEVerifying(false);
      }
    };

    const priorityColumn = mappingState.columns.find(
      (column) => column.isPriority
    );

    const columns = useMemo(() => {
      const mappingDataColumns = Array.from({
        length: mappingState.columnNumber
      }).map((_, index): MUIDataTableColumnDef => {
        const currentMapping = mappingState.columns.find(({ columnId }) =>
          columnId?.includes(index)
        );
        
        // Si es una columna especial (prioridad), crear el checkbox
        if (currentMapping?.isPriority && priorityColumn) {
          return {
            name: `column${index}`,
            label: 'Prioritario',
            options: {
              customBodyRender: (_, tableMeta) => {
                const rowIndex = tableMeta.rowIndex;
                return (
                  <Checkbox
                    checked={priorityStatus[rowIndex]}
                    onChange={(e) => {
                      const newPriorityStatus = [...priorityStatus];
                      newPriorityStatus[rowIndex] = e.target.checked;
                      setPriorityStatus(newPriorityStatus);

                      // Guardar el valor en los datos de mapeo
                      setMappingData((prev) => {
                        const newData = [...prev];
                        newData[rowIndex] = {
                          ...newData[rowIndex],
                          [`column${index}`]: e.target.checked ? "1" : "0"
                        };
                        return newData;
                      });
                    }}
                    color="primary"
                  />
                );
              },
              customHeadRender: () => {
                return (
                  <th key={uuidv4()} style={{ minWidth: '80px' }}>
                    <span className="text-xs">Prioritario</span>
                  </th>
                );
              }
            }
          };
        }

        // Para columnas normales (no especiales)
        return {
          name: `column${index}`,
          label:
            mappingState.columns.find(({ columnId }) =>
              columnId?.includes(index)
            )?.label || `Sin Asignar`,
          options: {
            customBodyRender: (info) => {
              return <div>{info}</div>;
            },
            customHeadRender: () => {
              const currentMapping = mappingState.columns.find(({ columnId }) =>
                columnId?.includes(index)
              );
              return (
                <HeaderSelector
                  key={uuidv4()}
                  currentMapping={currentMapping}
                  currentColIndex={index}
                  onChange={handleChange}
                  onRemoveHeaderMapping={handleRemoveHeaderMapping}
                  columns={mappingState.columns}
                />
              );
            }
          }
        };
      });
      return mappingDataColumns;
    }, [
      mappingState.columnNumber,
      mappingState.columns,
      priorityStatus,
      priorityColumn,
      regressStatus,
    ]);

    useImperativeHandle(ref, () => ({
      onClick: async () => {
        await handleSubmit();
      }
    }));

    const travelDataReduced = useMemo(
      () => mappingData.slice(0, 5),
      [mappingData]
    );

    return (
      <>
        <Table
          title="Mapeo de columnas"
          options={{
            elevation: 0,
            selectableRows: 'none',
            selectToolbarPlacement: 'none',
            search: false,
            filter: false,
            sort: false,
            download: false,
            print: false,
            fixedSelectColumn: true,
            pagination: false,
            rowsPerPageOptions: [5],
            customToolbar: () => {
              return (
                <div className="custom-toolbar">
                  <Button
                    onClick={handleResetMapping}
                    shape="circle"
                    color="gray"
                    className="hover:bg-primary-400"
                  >
                    <Tooltip title="Descartar todo">
                      <RestoreIcon fontSize="medium" />
                    </Tooltip>
                  </Button>
                </div>
              );
            }
          }}
          columns={columns}
          data={travelDataReduced}
        />
        {isMissingColumnOpen && (
          <Modal
            open
            size="sm"
            subtitle="Asigne las siguientes columnas para continuar"
            title="Error de Mapeo"
            onClose={() => setIsMissingColumnOpen(false)}
            body={
              <div className="p-4 flex flex-wrap gap-x-4 gap-y-6">
                {mappingState.columns
                  .filter(({ mandatory, inUse }) => mandatory && !inUse)
                  .map(({ label }) => (
                    <Chip
                      label={label}
                      variant="outlined"
                      color="secondary"
                      key={label}
                    />
                  ))}
              </div>
            }
          />
        )}
        {isBEVerifying && (
          <div
            className="fixed w-full h-full flex top-0 left-0 justify-center items-center"
            style={{
              backgroundColor: '#cbc3c3a1',
              zIndex: 1000
            }}
          >
            <div className="bg-white rounded px-4">
              <Loading
                label={
                  <div className="mr-4">
                    <p className="text-black">Validando datos</p>
                    <p className="text-sm">
                      El proceso puede demorar unos instantes
                    </p>
                  </div>
                }
              />
            </div>
          </div>
        )}
      </>
    );
  }
);

export default TravelFormMapping;
